/**
 * Which jobs have just asked the backend for an auto-invoice.
 *
 * The billing tab polls for an invoice that is on its way, and it has to know
 * when one actually is. Completion is one trigger; switching
 * "Auto-invoice on completion" on for a job that already finished is the other,
 * and that request is served asynchronously so there is nothing to read yet.
 *
 * A recent `updatedAt` is NOT the signal — it is far broader than the backend's
 * behaviour. The backend publishes only on the false -> true flip, so editing
 * notes on a long-completed job whose flag is already on requests nothing, and
 * treating that as "generating" would claim work that will never arrive.
 *
 * So the save path records the flip it actually made, here. In-memory and
 * session-scoped on purpose: this answers "did THIS tab just ask", and the
 * window it feeds is minutes long.
 */
const requestedAt = new Map<string, number>();

export function markAutoInvoiceRequested(jobId: string, now = Date.now()): void {
  requestedAt.set(jobId, now);
}

export function autoInvoiceRequestedWithin(jobId: string, windowMs: number): boolean {
  const at = requestedAt.get(jobId);
  if (at == null) return false;
  const age = Date.now() - at;
  if (age > windowMs) {
    requestedAt.delete(jobId);
    return false;
  }
  return age >= 0;
}

/** Test seam — the map outlives a single render tree. */
export function resetAutoInvoiceRequests(): void {
  requestedAt.clear();
}
